/**
 * The cancel dialog's plain-words refund copy (DNKADM-0445). Staff were picking a refund
 * from mechanism labels ("Policy (lead-time)", "Full (force)") with no idea what the
 * member would get back. This module turns the three admin cancellation previews (one per
 * choice: `policy`, `full`, `none`, the same math as the cancel) plus the reservation
 * into:
 *  - one sentence on how the booking was paid ({@link describePayment});
 *  - when it starts, in words ({@link describeTiming});
 *  - the Refund dropdown's options, each labelled with its OWN preview's outcome
 *    ({@link buildRefundOptions});
 *  - for a declined pending request, the one sentence on what it gives back
 *    ({@link describeDecline}, DNKADM-0446).
 *
 * Labels only: the option VALUES are the unchanged `policy | full | none` the cancel sends.
 * No client-side refund math: every amount is read from a preview, never computed here.
 *
 * DNKADM-0447 (DNKAPI-0653 preview fields): a credit line names the credit type
 * (`bucketName`, "1 credit" when the preview has none), the timing sentence names the
 * policy window (`policyWindowHours`, omitted when null) and a refund-locked booking gets
 * one plain line ({@link REFUND_LOCKED_NOTE}). The preview carries no card brand or last
 * four digits, so a card refund reads "to the original card"; a desk payment keeps its
 * stored method label.
 *
 * Kept out of the dialog file so that file only exports components (Fast Refresh).
 */
import type { CancellationPreview, RefundDirective, Reservation } from '@/api/reservations';
import { formatCreditAmount, formatMoney, minorUnitFactor } from '@/utils/v2Format';

/** The three previews, one per refund choice. */
export type CancelPreviews = Record<RefundDirective, CancellationPreview>;

/** One Refund dropdown option: the unchanged choice value + its outcome label. */
export interface RefundOption {
  value: RefundDirective;
  label: string;
}

/** What one choice gives the member back, normalized from its preview. */
type Outcome =
  | { kind: 'credits'; credits: number; bucketName: string | null }
  | { kind: 'money'; amount: string | null; percent: number | null }
  | { kind: 'review' }
  | { kind: 'nothing' };

/** Credits without raw decimals: 1 → "1", 0.5 → "0.5". */
function formatCount(n: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
}

/** "1 credit" / "2 Lessons credits" / "0.5 Lesson Credits" (the shared DNKADM-0454 wording). */
function creditsText(n: number, bucketName: string | null): string {
  return formatCreditAmount(n, bucketName);
}

/** A minor-unit amount as money, or null when the amount or its currency is unknown. */
function moneyText(minor: number | null, currency: string | null): string | null {
  if (minor === null || !currency) return null;
  return formatMoney(minor / minorUnitFactor(currency), currency);
}

/** The stored tenant method label (a desk-recorded payment), or null for a card charge. */
function methodLabelOf(previews: CancelPreviews): string | null {
  return (
    previews.policy.paymentMethodLabel ??
    previews.full.paymentMethodLabel ??
    previews.none.paymentMethodLabel ??
    null
  );
}

/** How the booking was paid, for "Paid $49.99 by ...": the method label, else "card". */
function methodText(previews: CancelPreviews): string {
  return methodLabelOf(previews) ?? 'card';
}

/** Where a refund goes, for "Refund $49.99 to ...": the method label, else "the original card". */
function refundTargetText(previews: CancelPreviews): string {
  return methodLabelOf(previews) ?? 'the original card';
}

/** The credit type's name, when any preview's bucket change carries one. */
function bucketNameOf(previews: CancelPreviews): string | null {
  for (const p of [previews.full, previews.policy, previews.none]) {
    const named = p.bucketChanges.find((b) => b.bucketName !== null);
    if (named) return named.bucketName;
  }
  return null;
}

function outcomeOf(p: CancellationPreview, bucketName: string | null): Outcome {
  switch (p.outcome) {
    case 'MONEY_REFUND':
      if (p.refundAmountMinor === 0) return { kind: 'nothing' };
      return {
        kind: 'money',
        amount: moneyText(p.refundAmountMinor, p.currency),
        percent: p.refundPercent,
      };
    case 'MONEY_REFUND_REVIEW':
      return { kind: 'review' };
    case 'MONEY_NO_REFUND':
    case 'NO_CHARGE':
    case 'NO_REFUND':
    case 'REFUND_LOCKED':
      return { kind: 'nothing' };
    default: {
      // FULL_REFUND / PARTIAL_REFUND (or an unrecognised token): the credits handed back.
      const fromBuckets = p.bucketChanges.reduce((sum, b) => sum + b.delta, 0);
      const credits = fromBuckets > 0 ? fromBuckets : (p.summary?.refund ?? 0);
      return credits > 0 ? { kind: 'credits', credits, bucketName } : { kind: 'nothing' };
    }
  }
}

/** A stable identity for an outcome, so equal choices collapse into one option. */
function keyOf(o: Outcome): string {
  switch (o.kind) {
    case 'credits':
      return `credits:${o.credits}`;
    case 'money':
      return `money:${o.amount ?? '?'}:${o.percent ?? '?'}`;
    default:
      return o.kind;
  }
}

/** What comes back, without the verb: "1 credit", "$24.99 (50%) to the original card". */
function amountText(o: Outcome, method: string): string {
  switch (o.kind) {
    case 'credits':
      return creditsText(o.credits, o.bucketName);
    case 'money': {
      if (o.amount === null) return `to ${method}`;
      const pct =
        o.percent !== null && Math.round(o.percent) !== 100 ? ` (${Math.round(o.percent)}%)` : '';
      return `${o.amount}${pct} to ${method}`;
    }
    case 'review':
      return 'after a staff review';
    case 'nothing':
      return 'nothing';
  }
}

/** The outcome as a lower-case phrase: "give back 1 credit", "refund $49.99 to the original card", "nothing back". */
function phraseOf(o: Outcome, method: string): string {
  switch (o.kind) {
    case 'credits':
      return `give back ${amountText(o, method)}`;
    case 'money':
    case 'review':
      return `refund ${amountText(o, method)}`;
    case 'nothing':
      return 'nothing back';
  }
}

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** The refund-lock line (a rescheduled booking): the member's own cancel gets nothing, staff Full still does. */
export const REFUND_LOCKED_NOTE =
  'The member’s own cancel would give nothing back (rescheduled booking). Full still gives everything back.';

/** The only Refund option for an unpaid booking: there is no payment to give back. */
export const UNPAID_NOTHING_TO_REFUND = 'Not paid yet. Nothing to refund.';

/** The info line when the previews could not be loaded. */
export const PREVIEW_FAILED_NOTE = 'Couldn’t load the refund amounts.';

/** The options shown when the previews could not be loaded (cancel still works). */
export const FALLBACK_REFUND_OPTIONS: readonly RefundOption[] = [
  { value: 'policy', label: 'Follow cancellation policy' },
  { value: 'full', label: 'Give back in full' },
  { value: 'none', label: 'Nothing back' },
];

/**
 * The Refund dropdown's options, each labelled from its own preview. The policy choice
 * always shows and says what the policy gives. A choice whose outcome equals the policy's
 * is dropped (policy = full shows policy + "Nothing back"; policy = nothing shows policy +
 * "Give back anyway: ..."). `previews` null (loading or failed) → the generic fallback.
 */
export function buildRefundOptions(
  previews: CancelPreviews | null,
  unpaid = false,
): readonly RefundOption[] {
  if (!previews) return FALLBACK_REFUND_OPTIONS;
  const method = refundTargetText(previews);
  const bucketName = bucketNameOf(previews);
  const policy = outcomeOf(previews.policy, bucketName);
  const full = outcomeOf(previews.full, bucketName);
  const none = outcomeOf(previews.none, bucketName);
  // DNKADM-0474: an unpaid (Pay later) booking has nothing to give back whichever choice is
  // made, so "Follow cancellation policy: nothing back" read as the studio keeping money.
  if (unpaid && policy.kind === 'nothing' && full.kind === 'nothing' && none.kind === 'nothing') {
    return [{ value: 'policy', label: UNPAID_NOTHING_TO_REFUND }];
  }
  // A credit booking the policy keeps the credit for names the credit type that is kept.
  const kept =
    policy.kind === 'nothing' &&
    bucketName !== null &&
    isCreditOutcome(previews.policy) &&
    (previews.policy.summary?.debited ?? 0) > 0
      ? ` (${creditsText(previews.policy.summary?.debited ?? 0, bucketName)} kept)`
      : '';
  const options: RefundOption[] = [
    { value: 'policy', label: `Follow cancellation policy: ${phraseOf(policy, method)}${kept}` },
  ];
  if (keyOf(full) !== keyOf(policy)) {
    options.push({
      value: 'full',
      label:
        policy.kind === 'nothing'
          ? `Give back anyway: ${full.kind === 'review' ? phraseOf(full, method) : amountText(full, method)}`
          : cap(phraseOf(full, method)),
    });
  }
  if (keyOf(none) !== keyOf(policy)) {
    options.push({ value: 'none', label: cap(phraseOf(none, method)) });
  }
  return options;
}

/**
 * DNKADM-0452 (DNKAPI-0658): a refund of the booking’s charge is already processing, read from
 * the previews (each carries the same fields). Null when none is, or the API predates the
 * fields. The amount is the API’s (major units, null when unknown); nothing is computed here.
 */
export function refundInProgressOf(
  previews: CancelPreviews,
): { amount: number | null; currency: string | null } | null {
  const flagged = [previews.policy, previews.full, previews.none].filter(
    (p) => p.refundInProgress === true,
  );
  if (flagged.length === 0) return null;
  return {
    amount: flagged.find((p) => p.refundInProgressAmount != null)?.refundInProgressAmount ?? null,
    currency: flagged.find((p) => p.currency)?.currency ?? null,
  };
}

/**
 * DNKADM-0452: true when declining (the `full` preview) would send money back, a money refund
 * or one held for review. A decline that gives credits back or has nothing to refund is false.
 */
export function declineSendsMoney(full: CancellationPreview): boolean {
  const kind = outcomeOf(full, null).kind;
  return kind === 'money' || kind === 'review';
}

/**
 * True when this choice would send money back (a money refund, or one held for review), so a
 * refund already processing must disable it. Credits-only and nothing-back choices are false.
 */
export function isMoneyRefundChoice(previews: CancelPreviews, value: RefundDirective): boolean {
  const kind = outcomeOf(previews[value], null).kind;
  return kind === 'money' || kind === 'review';
}

/** True when the policy gives back less than a full refund would (a warning, not info). */
export function policyFallsShort(previews: CancelPreviews): boolean {
  const bucketName = bucketNameOf(previews);
  return (
    keyOf(outcomeOf(previews.policy, bucketName)) !== keyOf(outcomeOf(previews.full, bucketName))
  );
}

/** True when the policy preview gives the same as a full refund: the cancel is outside the window. */
export function policyGivesFullRefund(previews: CancelPreviews): boolean {
  return !policyFallsShort(previews);
}

const isMoneyOutcome = (p: CancellationPreview): boolean =>
  p.outcome?.startsWith('MONEY_') ?? false;
const isCreditOutcome = (p: CancellationPreview): boolean =>
  p.outcome === 'FULL_REFUND' ||
  p.outcome === 'PARTIAL_REFUND' ||
  p.outcome === 'NO_REFUND' ||
  p.outcome === 'REFUND_LOCKED';

/**
 * One sentence on how the booking was paid, or null when the facts do not say. Pay later
 * reads the reservation (`paymentStatus: 'unpaid'` + `amountDue`), since an unpaid booking
 * previews `NO_CHARGE` exactly like a free one; everything else reads the previews.
 */
export function describePayment(reservation: Reservation, previews: CancelPreviews): string | null {
  const all = [previews.policy, previews.full, previews.none];

  if (reservation.paymentStatus?.trim().toLowerCase() === 'unpaid') {
    const due =
      reservation.amountDue != null && reservation.currency
        ? formatMoney(reservation.amountDue, reservation.currency)
        : null;
    return due
      ? `Not paid yet. Cancelling clears the ${due} balance.`
      : 'Not paid yet. Cancelling clears the balance.';
  }

  if (all.some(isMoneyOutcome)) {
    const currency = all.find((p) => p.currency)?.currency ?? null;
    const paidMinor =
      all.find((p) => p.paidAmountMinor !== null)?.paidAmountMinor ??
      (previews.full.outcome === 'MONEY_REFUND' && (previews.full.refundPercent ?? 100) === 100
        ? previews.full.refundAmountMinor
        : null);
    const paid = moneyText(paidMinor, currency);
    const method = methodText(previews);
    return paid ? `Paid ${paid} by ${method}.` : `Paid by ${method}.`;
  }

  const debited =
    previews.full.summary?.debited ||
    previews.policy.summary?.debited ||
    reservation.creditsCharged ||
    0;
  if (debited > 0 || all.some(isCreditOutcome)) {
    return debited > 0
      ? `Booked with ${creditsText(debited, bucketNameOf(previews))}.`
      : 'Booked with credits.';
  }

  if (all.every((p) => p.outcome === 'NO_CHARGE')) {
    if (reservation.paymentMode === 'card_on_file') return 'Card on file, not charged yet.';
    if (reservation.paymentStatus == null) return 'Booked at no charge.';
  }
  return null;
}

/** "41 min" / "18 h 41 min" / "3 days". */
function durationText(hours: number): string {
  const minutes = Math.round(hours * 60);
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 48 * 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m === 0 ? `${h} h` : `${h} h ${m} min`;
  }
  return `${Math.floor(minutes / (24 * 60))} days`;
}

/**
 * When the booking starts, in words, from the preview's lead hours; null when unknown.
 * With the policy window (`policyWindowHours`, the smallest lead time that gives a full
 * refund) it also says where the cancel sits against it: "18 h 41 min before start, inside
 * the 24-hour window." / "2 days before start, outside the 24-hour window.". "Outside" means
 * the policy preview gives a full refund (`policyGivesFull`), "inside" otherwise; the lead
 * time is never compared to the window on the client (DNKADM-0450). A null window
 * (or a session already started) omits the clause: "Starts in 18 h 41 min.". The window is
 * shown as the API gives it.
 */
export function describeTiming(
  leadHours: number | null,
  policyWindowHours: number | null = null,
  policyGivesFull = false,
): string | null {
  if (leadHours === null) return null;
  if (leadHours < 0) return `Started ${durationText(-leadHours)} ago.`;
  if (policyWindowHours === null) return `Starts in ${durationText(leadHours)}.`;
  // DNKADM-0450: the preview decides, never a lead-time compare here (a cancel exactly at the
  // boundary reads whatever dink-api's refund says).
  const where = policyGivesFull ? 'outside' : 'inside';
  return `${durationText(leadHours)} before start, ${where} the ${formatCount(policyWindowHours)}-hour window.`;
}

/**
 * The decline sentence (DNKADM-0446). Declining a pending request always sends `full` (the
 * studio refused the session; the member did nothing wrong), so the decline dialog has no
 * Refund choice and instead says what `full` gives back, read from the `full` preview:
 * "Declining gives back 1 Lessons credit." / "Declining refunds $49.99 to the card." /
 * "Nothing was charged.". No client-side refund math: every amount is the preview's. A
 * preview that could not be loaded is the caller's to word ({@link DECLINE_PREVIEW_FAILED_NOTE}).
 */
export function describeDecline(full: CancellationPreview): string {
  const bucketName = full.bucketChanges.find((b) => b.bucketName !== null)?.bucketName ?? null;
  const o = outcomeOf(full, bucketName);
  switch (o.kind) {
    case 'credits':
      return `Declining gives back ${creditsText(o.credits, o.bucketName)}.`;
    case 'money': {
      const to = full.paymentMethodLabel ? `paid by ${full.paymentMethodLabel}` : 'to the card';
      return o.amount === null ? `Declining refunds the payment ${to}.` : `Declining refunds ${o.amount} ${to}.`;
    }
    case 'review':
      return 'Declining refunds the payment after a staff review.';
    case 'nothing':
      // A free or unpaid booking (or a credit booking that drew nothing) had nothing taken.
      return full.outcome === 'NO_CHARGE' ||
        full.outcome === 'MONEY_REFUND' ||
        (!isMoneyOutcome(full) && (full.summary?.debited ?? 0) === 0)
        ? 'Nothing was charged.'
        : 'Declining gives nothing back.';
  }
}

/** The decline dialog's line when the `full` preview could not be loaded. */
export const DECLINE_PREVIEW_FAILED_NOTE = 'Couldn’t load the refund amounts. Declining gives back in full.';
